import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BTN, BTN_XS } from '../../constants/buttons';
import TaskTable, { taskTableColumns, sortTasks, nextTaskSort } from '../tasks/TaskTable';
import { useIsMobileOrderViewport } from '../../hooks/useIsMobileOrderViewport';
import useOpenAtTop from '../../hooks/useOpenAtTop';
import TaskKanbanView from './TaskKanbanView';
import CrossTeamGanttView from './CrossTeamGanttView';
import { boardBaselineCanEdit } from './taskEditBaseline';
import TaskEditor from './TaskEditor';
import TaskViewSwitcher from './TaskViewSwitcher';
import { SEARCH_BOX_WIDTH } from '../../constants/inputs';
import EmptyState from '../EmptyState';
import ConfirmDialog from '../ConfirmDialog';
import { toBody } from '../JoinDepartmentDialog';
import useDirtyPrompt from '../../hooks/useDirtyPrompt';
import { UNSAVED_PROMPT_BUTTONS } from '../../constants/unsavedChangesPrompt';
import { MODAL_OVERLAY, MODAL_PANEL_MOBILE_CAP } from '../../constants/overlayLayers';

/**
 * Internal task-editor modal (folds in the gag panel's former GagTaskModal) —
 * thin chrome (overlay, heading, Escape / backdrop close) around the shared
 * TaskEditor in mode='modal'. Opened from a Kanban card or a Gantt bar click.
 * The network is injected via onSave/onDelete (the board's onUpdateTask/
 * onDeleteTask closures) so this modal makes NO calls itself. busy is reported
 * up from the editor so Escape / backdrop clicks can't dismiss it mid-save.
 */
function TaskBoardModal({
  task, canEdit, canAssign, assignPicker, readOnlyNote, subDepts, assignableUsers,
  onSave, onDelete, onClose, onSaved, onDeleted, taskExtras,
}) {
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 ${MODAL_OVERLAY}`}
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose?.(); }}
      role="dialog"
      aria-modal="true"
      aria-label="Edit task"
    >
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-5 ${MODAL_PANEL_MOBILE_CAP}`}>
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-3">
          {canEdit ? 'Edit Task' : 'Task'}
        </h3>
        <TaskEditor
          task={task}
          canEdit={canEdit}
          canAssign={canAssign}
          assignPicker={assignPicker}
          {...(readOnlyNote !== undefined ? { readOnlyNote } : {})}
          subDepts={subDepts}
          assignableUsers={assignableUsers}
          mode="modal"
          onSave={onSave}
          onDelete={onDelete}
          onCancel={onClose}
          onSaved={onSaved}
          onDeleted={onDeleted}
          onBusyChange={setBusy}
          taskExtras={taskExtras}
        />
        {/* Read-only viewers get no editor footer — give them a Close. */}
        {!canEdit && (
          <div className="flex justify-end mt-4">
            <button type="button" onClick={() => onClose?.()} className={BTN.secondary}>
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Shared, data-source-agnostic task board: view switcher (List / Kanban /
 * Gantt) + rendering + inline quick-add + the Kanban/Gantt-click editor modal.
 * It owns NO network — every mutation is a prop callback, so the same board
 * serves gag tasks (/api/gags/...) and team tasks (/api/teams/...) unchanged.
 *
 * Props:
 *  - tasks: the task rows to render.
 *  - milestones (OPTIONAL): array of { field, label, shape, date, draggable }
 *    for the Gantt milestone lane. Omit for teams; pass the derived array for
 *    gags (built from GagGanttView's MILESTONE_DEFS). The lane + onMilestoneDrag
 *    are wired ONLY when this prop is provided.
 *  - groupLabel: label for the single synthetic Gantt group every task is
 *    pinned to (the gag/team title).
 *  - canEdit: bool | (task) => bool — the PER-TASK edit predicate used by the
 *    inline List editor and the modal (lets a caller express the gag panel's
 *    "manager OR task creator" rule as a function). Board-level rights (Kanban/
 *    Gantt drag, add-task default) use the baseline `canEdit` evaluated against
 *    a task with no creator (see baseCanEdit) so global drag stays gag-level —
 *    exactly as the gag panel gated it.
 *  - canAddTask (OPTIONAL, default canEdit===true): gates the quick-add input.
 *  - assignableUsers, subDepts: passed to the editors (subDepts [] hides the
 *    sub-department field).
 *  - view / onViewChange / initialView: controlled-with-fallback. When view +
 *    onViewChange are supplied the caller owns the view (and its persistence +
 *    mobile fallback); otherwise the board keeps internal state seeded from
 *    initialView and applies its own mobile Gantt→List fallback.
 *  - onCreateTask(title) => Promise<Response|void>: caller POSTs (seeding any
 *    view-dependent defaults itself, e.g. Gantt dates) + refetches, returns the
 *    Response so the board can surface a server error and keep the typed title.
 *  - onUpdateTask(task, patch) => Promise<Response|void>: caller PUTs + refetches.
 *  - onDeleteTask(task) => Promise<Response|void>: caller DELETEs + refetches.
 *  - onStatusChange(task, status) => Promise: Kanban drop. MUST resolve on
 *    success and REJECT/throw on failure (the Kanban reverts on rejection).
 *  - onDragComplete(task, start, due) => Promise: Gantt bar reschedule.
 *  - onMilestoneDrag(field, value) => Promise: Gantt milestone move (only
 *    meaningful when milestones is passed).
 *  - onDirtyChange(bool): bubbles the open inline List editor's dirty state up
 *    (wire to the parent's collapse guard).
 *  - showDirtyWarning: the parent's pulse (GagGroupTable's failed
 *    Save-and-close, via GagDetailPanel), passed to the inline editor.
 *  - saveRequestRef / discardRequestRef (OPTIONAL, 2026-10-07): published as
 *    the OPEN inline editor's save (`() => Promise<boolean>`, true only once
 *    nothing is unsaved) and discard — TaskEditor's own `save` / `revert`,
 *    never a second path — so a parent prompt (GagDetailPanel's `submitRef`)
 *    can save or drop a dirty task edit.
 *  - collapseRequestRef (OPTIONAL, 2026-10-19): published as
 *    `(onClosed) => void` — close every open task (List row + modal), asking
 *    first when the List editor is dirty, then `onClosed()`; never on Keep
 *    editing or a failed save. The gag panel's "Task planning" fold.
 *  - taskExtras(task) => ReactNode (OPTIONAL): per-task extras rendered inside
 *    the inline List editor AND the Kanban/Gantt-click modal (e.g. the team
 *    surface's comments thread + file uploads). Omitted on the gag surface →
 *    nothing extra renders and both editors stay byte-identical to before.
 *  - addTaskPlacement: 'top' | 'bottom' (default 'top'). 'top' keeps the inline
 *    quick-add in the header toolbar (the gag surface's behaviour — unchanged).
 *    'bottom' moves it under the LIST view; on Kanban/Gantt it stays in the
 *    header (so it's still reachable there).
 */
export default function TeamTaskBoard({
  tasks,
  milestones,
  groupLabel,
  canEdit,
  canAddTask,
  assignableUsers,
  subDepts,
  view,
  onViewChange,
  initialView = 'gantt',
  onCreateTask,
  onUpdateTask,
  onDeleteTask,
  onStatusChange,
  onDragComplete,
  onMilestoneDrag,
  onDirtyChange,
  showDirtyWarning,
  taskExtras,
  addTaskPlacement = 'top',
  // ── Gags/teams/tasks Stage 1 (2026-10-02), all OPTIONAL. ──
  // canAssign: bool | (task) => bool — may the caller change a task's
  // assignees (rule 3)? Defaults to the edit predicate (the old behaviour).
  canAssign,
  // assignPicker: passed to TaskEditor — the team-first UserAutocomplete.
  assignPicker = null,
  // The LIST view is the shared `tasks/TaskTable` since Stage 1 (it replaced
  // TaskListView); `hideColumns` drops columns that say nothing here (the gag
  // panel hides `context` — every row is that gag's).
  hideColumns = [],
  readOnlyNote,
  saveRequestRef = null,
  discardRequestRef = null,
  // collapseRequestRef (OPTIONAL, 2026-10-19): see the publishing effect below.
  // Not passed → nothing is published and the board is unchanged.
  collapseRequestRef = null,
  // headerLeadRef (OPTIONAL): a ref callback for an empty slot at the START of
  // the header line, where a page's search box sits — the gag panel portals
  // its "add to the team" input into it. Not passed → no slot.
  headerLeadRef = null,
  viewTabsIdPrefix = 'task-view',
  // initialExpandedId (OPTIONAL): the task to open on mount in the LIST view
  // (the Breakdown's Labour view opens the clicked task's editor, 2026-10-14).
  initialExpandedId = null,
  // hideEditorDirtyChip (OPTIONAL): passed to the inline TaskEditor as
  // `hideDirtyChip` — the gag panel under an editing desktop row (2026-10-18).
  hideEditorDirtyChip = false,
}) {
  const isMobile = useIsMobileOrderViewport();
  // ── View: controlled if view + onViewChange supplied, else internal. ──
  const controlled = view != null && typeof onViewChange === 'function';
  const [internalView, setInternalView] = useState(initialView);
  const activeView = controlled ? view : internalView;
  const setView = (v) => { if (controlled) onViewChange(v); else setInternalView(v); };

  // Mobile Gantt→List fallback for the UNCONTROLLED case only — a controlled
  // caller (the gag panel) owns this so its desktop preference survives.
  useEffect(() => {
    if (controlled) return undefined;
    const check = () => {
      if (internalView === 'gantt' && window.innerWidth < 768) setInternalView('list');
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [controlled, internalView]);

  // ── Permission resolution ──
  // Per-task (inline List + modal): the caller's bool/fn, evaluated per row.
  const resolveCanEdit = (task) => (typeof canEdit === 'function' ? !!canEdit(task) : !!canEdit);
  // Board-level baseline (Kanban/Gantt global drag + add-task): a task with no
  // creator can't match a "created_by === me" clause, so a per-task predicate
  // collapses to its manager-level base here — matching the gag panel, which
  // gated drag on the gag-level flag while editing rights were per-task.
  const baseCanEdit = boardBaselineCanEdit(canEdit);
  const addTaskAllowed = canAddTask !== undefined ? canAddTask : canEdit === true;
  const resolveCanAssign = (task) => (canAssign === undefined ? resolveCanEdit(task)
    : (typeof canAssign === 'function' ? !!canAssign(task) : !!canAssign));

  // ── LIST view state (moved here from TaskListView with the TaskTable
  // switch): one expanded row; the other rows dim (`PageScrollTable`'s
  // editingRowId) while its inline editor is dirty, and the dirt is reported up
  // so the gag panel's own collapse respects it.
  // 🔀 ASK-THEN-CONTINUE (owner 2026-10-07, "A, convert all three"): while that
  // editor is dirty, collapsing it or opening another row raises the three-way
  // prompt (`hooks/useDirtyPrompt.js`), then runs the click on Save (ok)
  // or Discard — never on Keep editing or a failed save. It used to REFUSE and
  // pulse amber (`rowWarn`, deleted; pre-deletion SHA 5b4ed62c).
  const [expandedId, setExpandedId] = useState(initialExpandedId);
  const [rowDirty, setRowDirty] = useState(false);
  const [sortState, setSortState] = useState({ field: 'due_date', dir: 'asc' });
  useEffect(() => { onDirtyChange?.(rowDirty); }, [rowDirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);
  // `saveRef` / `discardRef` take the OPEN editor's own save / discard
  // (TaskEditor's `save` / `revert`); `hooks/useDirtyPrompt.js` does the rest.
  const {
    continueWhenClean, saveRef: editorSaveRef, discardRef: editorDiscardRef, promptProps,
  } = useDirtyPrompt({ dirty: rowDirty });
  // Published for a PARENT's prompt (GagDetailPanel's `submitRef`): nothing
  // dirty → true; otherwise the open editor's own save.
  useEffect(() => {
    if (!saveRequestRef) return undefined;
    // `opts` ({ onError }) passes straight to TaskEditor's request.
    const request = (opts) => {
      if (!rowDirty) return Promise.resolve(true);
      return editorSaveRef.current ? editorSaveRef.current(opts) : Promise.resolve(false);
    };
    saveRequestRef.current = request;
    return () => { if (saveRequestRef.current === request) saveRequestRef.current = null; };
  });
  useEffect(() => {
    if (!discardRequestRef) return undefined;
    const request = () => { editorDiscardRef.current?.(); };
    discardRequestRef.current = request;
    return () => { if (discardRequestRef.current === request) discardRequestRef.current = null; };
  });
  // Published for a PARENT that hides the whole board (GagDetailPanel folding
  // "Task planning", owner 2026-10-19). `request(onClosed)` closes every open
  // task — the List row AND a Kanban/Gantt modal — then calls `onClosed`. A
  // dirty List editor ASKS first through THIS board's own prompt (the
  // `toggleRow` path): Save = TaskEditor's `save`, Discard = its `revert`;
  // Keep editing or a failed save drop the request, so `onClosed` never runs.
  useEffect(() => {
    if (!collapseRequestRef) return undefined;
    const request = (onClosed) => continueWhenClean(() => {
      setClosingId(null);
      setExpandedId(null);
      setModalTask(null);
      onClosed?.();
    });
    collapseRequestRef.current = request;
    return () => { if (collapseRequestRef.current === request) collapseRequestRef.current = null; };
  });
  // 📱 Opening a task on a PHONE (owner 2026-10-05, the order line items' rule):
  // it scrolls to the top first, and only then does the one that was open close
  // (`hooks/useOpenAtTop.js`); `closingId` keeps that one rendered open meanwhile.
  // A dirty editor asks first (above), so no edit can be lost.
  const [closingId, setClosingId] = useState(null);
  const boardRef = useRef(null);
  const openAtTop = useOpenAtTop();
  // A held click re-enters the LATEST handler (the hook's docblock, point 2).
  const toggleRowRef = useRef(null);
  const toggleRow = (task) => {
    if (rowDirty) { continueWhenClean(() => toggleRowRef.current(task)); return; }
    if (isMobile && activeView === 'list' && expandedId !== task.id) {
      const closing = expandedId;
      setClosingId(closing);
      setExpandedId(task.id);
      const row = boardRef.current
        ? boardRef.current.querySelector(`[data-testid="board-task-row-${task.id}"]`) : null;
      // Clears only ITS OWN closing row: a late finish from an earlier, faster
      // tap must not close the row a newer open is keeping.
      openAtTop(row, () => setClosingId((cur) => (cur === closing ? null : cur)));
      return;
    }
    setClosingId(null);
    setExpandedId((cur) => (cur === task.id ? null : task.id));
  };
  toggleRowRef.current = toggleRow;
  const onHeaderSort = (field) => setSortState((s) => nextTaskSort(s, field));
  const listRows = useMemo(() => sortTasks(tasks, sortState.field, sortState.dir), [tasks, sortState]);

  // ── Internal editor modal (Kanban / Gantt clicks) ──
  const [modalTask, setModalTask] = useState(null);

  // ── Inline quick-add (title-only; full fields via the editor after add) ──
  const [addTitle, setAddTitle] = useState('');
  const [addBusy, setAddBusy] = useState(false);
  const [addHint, setAddHint] = useState(false); // empty-title validation hint
  const [addError, setAddError] = useState(null);
  const addInputRef = useRef(null);

  const addTask = async () => {
    const title = addTitle.trim();
    // Empty/whitespace-only title: inline hint + refocus, no create.
    if (!title) { setAddHint(true); addInputRef.current?.focus(); return; }
    if (addBusy) return;
    setAddBusy(true);
    setAddHint(false);
    setAddError(null);
    try {
      const res = await onCreateTask(title);
      if (res && !res.ok) {
        const data = await res.json().catch(() => ({}));
        setAddError(data.error || 'Failed to add task');
      } else {
        setAddTitle('');
      }
    } catch {
      setAddError('Failed to add task — check your connection.');
    }
    setAddBusy(false);
  };

  const hasMilestoneDates = Array.isArray(milestones) && milestones.length > 0;

  // ── Gantt: pin every task to one synthetic group (folds in GagGanttView) ──
  // Keep each task's real team_color; only override team_id/team_name so the
  // whole board renders as one collapsible group labelled groupLabel.
  const mappedTasks = useMemo(
    () => tasks.map((t) => ({ ...t, team_id: 0, team_name: groupLabel })),
    [tasks, groupLabel]
  );
  const hasDated = mappedTasks.some((t) => t.start_date || t.due_date);

  const ganttBlock = (!hasDated && !hasMilestoneDates) ? (
    <EmptyState
      size="sm"
      title="No tasks with dates yet"
      description="Add start/due dates to tasks to see them on the timeline."
    />
  ) : (
    <div className="space-y-2">
      {!hasDated && (
        <p className="text-xs text-gray-500 dark:text-gray-400 italic">
          No tasks with dates yet — add start/due dates to tasks to see them on the timeline.
        </p>
      )}
      <CrossTeamGanttView
        tasks={mappedTasks}
        onTaskClick={(t) => setModalTask(t)}
        canDrag={baseCanEdit}
        onDragComplete={onDragComplete}
        milestones={milestones}
        onMilestoneDrag={milestones ? onMilestoneDrag : undefined}
        showAssignees
        showSummary
        statusColorFallback
      />
    </div>
  );

  // ── Inline quick-add placement ──
  // 'top' (gag default) keeps it in the header toolbar. 'bottom' moves it under
  // the List view; on Kanban/Gantt it stays in the header so it's reachable.
  const showAddAtBottom = addTaskPlacement === 'bottom' && activeView === 'list';
  const showAddInHeader = addTaskAllowed && !showAddAtBottom;

  const quickAddInner = addTaskAllowed ? (
    <>
      <div className="flex items-center gap-2">
        <input
          ref={addInputRef}
          type="text"
          value={addTitle}
          onChange={(e) => { setAddTitle(e.target.value); if (addHint) setAddHint(false); }}
          onKeyDown={(e) => { if (e.key === 'Enter') addTask(); }}
          placeholder="New task title…"
          maxLength={200}
          disabled={addBusy}
          aria-invalid={addHint}
          className={`px-3 py-1.5 text-sm rounded-lg border bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-blue-500 focus:outline-none w-44 md:w-56 ${
            addHint ? 'border-red-400 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'
          }`}
        />
        {/* Enabled even when empty so the click surfaces the hint. */}
        <button type="button" onClick={addTask} disabled={addBusy}
          title="Add a task" className={BTN_XS.primary}>
          {addBusy ? 'Adding…' : '+ Add'}
        </button>
      </div>
      {addHint && (
        <p className="mt-1 text-xs text-red-600 dark:text-red-400">Enter a task name</p>
      )}
    </>
  ) : null;

  const emptyAddHint = !addTaskAllowed ? undefined : (showAddAtBottom ? 'Add the first one below.' : 'Add the first one above.');

  return (
    <div ref={boardRef} className="space-y-3 min-w-0">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2" data-testid="task-board-header">
        {headerLeadRef && <div ref={headerLeadRef} className={SEARCH_BOX_WIDTH} data-testid="task-board-header-lead" />}
        <TaskViewSwitcher value={activeView} onChange={setView} idPrefix={viewTabsIdPrefix} />

        {/* Inline quick-add — full fields via the editor after creation. The
            add stays view-independent (available on List/Kanban/Gantt) so the
            Gantt path can seed default dates; empty-title clicks show an inline
            hint rather than doing nothing. Rendered in the header for the 'top'
            placement (and always on Kanban/Gantt); the 'bottom' List placement
            renders it under the list instead. */}
        {showAddInHeader && (
          <div className="ml-auto">
            {quickAddInner}
          </div>
        )}
      </div>

      {addError && (
        <div className="px-3 py-2 text-sm rounded-lg bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800">
          {addError}
        </div>
      )}

      {activeView === 'gantt' && (tasks.length > 0 || hasMilestoneDates) ? (
        /* Gantt renders for milestone-only sources too — List/Kanban keep the
           plain empty state below when there are no tasks. */
        ganttBlock
      ) : tasks.length === 0 ? (
        <EmptyState size="sm" title="No tasks yet" description={emptyAddHint} />
      ) : activeView === 'kanban' ? (
        <TaskKanbanView
          tasks={tasks}
          onTaskClick={(t) => setModalTask(t)}
          canDrag={baseCanEdit}
          onStatusChange={onStatusChange}
        />
      ) : (
        <TaskTable
          groups={[{ key: 'all', label: null, tasks: listRows }]}
          columns={taskTableColumns({ hide: hideColumns })}
          isMobile={isMobile}
          sort={{ field: sortState.field, dir: sortState.dir, onSort: onHeaderSort }}
          expandedId={expandedId}
          closingId={closingId}
          onToggle={toggleRow}
          editingRowId={rowDirty ? expandedId : null}
          testIdPrefix="board-task"
          phoneLift
          // The phone ⋯ sheet: both items open the row, whose editor holds the
          // assignee picker — Assign is offered only to someone who may assign.
          rowMenu={(t) => [
            { label: 'Open', onSelect: () => { if (expandedId !== t.id) toggleRow(t); } },
            ...(resolveCanEdit(t) && resolveCanAssign(t)
              ? [{ label: 'Assign', onSelect: () => { if (expandedId !== t.id) toggleRow(t); } }] : []),
          ]}
          // ONE editor implementation (TaskEditor); the per-task predicates
          // are the caller's.
          renderExpanded={(t) => (
            <TaskEditor
              task={t}
              canEdit={resolveCanEdit(t)}
              canAssign={resolveCanAssign(t)}
              assignPicker={assignPicker}
              readOnlyNote={readOnlyNote}
              subDepts={subDepts}
              assignableUsers={assignableUsers}
              mode="inline"
              onSave={(patch) => onUpdateTask(t, patch)}
              onDelete={() => onDeleteTask(t)}
              // Only the OPEN editor reports: the closing one unmounting must not
              // clear the dirt of the one just opened.
              onDirtyChange={t.id === expandedId ? setRowDirty : undefined}
              saveRequestRef={t.id === expandedId ? editorSaveRef : undefined}
              discardRequestRef={t.id === expandedId ? editorDiscardRef : undefined}
              showDirtyWarning={showDirtyWarning}
              hideDirtyChip={hideEditorDirtyChip}
              taskExtras={taskExtras}
            />
          )}
        />
      )}

      {/* Bottom-placement inline quick-add (List view only) — the team surface's
          "+ New Task at the bottom of the list" affordance. */}
      {showAddAtBottom && quickAddInner && (
        <div className="pt-1">
          {quickAddInner}
        </div>
      )}

      {/* ── Kanban / Gantt click → shared editor modal ── */}
      {modalTask && (
        <TaskBoardModal
          task={modalTask}
          canEdit={resolveCanEdit(modalTask)}
          canAssign={resolveCanAssign(modalTask)}
          assignPicker={assignPicker}
          readOnlyNote={readOnlyNote}
          subDepts={subDepts}
          assignableUsers={assignableUsers}
          onSave={(patch) => onUpdateTask(modalTask, patch)}
          onDelete={() => onDeleteTask(modalTask)}
          onClose={() => setModalTask(null)}
          onSaved={() => setModalTask(null)}
          onDeleted={() => setModalTask(null)}
          taskExtras={taskExtras}
        />
      )}

      {/* The three-way prompt over a dirty inline task edit — the order pages'
          and GagGroupTable's deck verbatim (Keep editing takes focus; Escape
          and the overlay both mean Keep editing). Portalled to <body>: the board
          renders inside an expanded table row, where sticky cells make stacking
          contexts the overlay must not sit under (`toBody`'s docblock). */}
      {toBody(
        <ConfirmDialog
          title="Unsaved changes to this task"
          message={`“${(tasks.find((x) => x.id === expandedId) || {}).title || 'This task'}” has edits that have not been saved.`}
          {...UNSAVED_PROMPT_BUTTONS}
          variant="save"
          {...promptProps}
        />
      )}
    </div>
  );
}
